import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Fieldset, Legend, Text, Heading } from '../components/ui'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/16/solid'
import { BRAND_COLOR } from '../constants'
import { useTheme } from '../hooks'
import { useAuthContext } from '../contexts/AuthContext'

const Login: React.FC = () => {
  const { darkMode } = useTheme()
  const navigate = useNavigate()
  const { signIn, loading, error, isAuthenticated } = useAuthContext()
  const [loginError, setLoginError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })

  // Redirecionar usuários já logados
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    setLoginError(null)
    const result = await signIn(formData.email, formData.password)
    
    if (result.success) {
      navigate('/dashboard')
    } else {
      setLoginError(result.error || 'Erro ao fazer login')
      console.error('Erro no login:', result.error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleForgotPassword = () => {
    navigate('/forgot-password')
  }

  const handleRegister = () => {
    navigate('/register')
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img 
            src="/logo-pasi.svg" 
            alt="PASI Logo" 
            className="w-12 h-12 rounded-lg object-contain"
          />
        </div>
        <Heading as="h2" size="2xl" className="mt-6 text-center">
          Pasi - QG de Agentes IA
        </Heading>
        <Text size="sm" color="muted" className="mt-2 text-center">
          Entrar na plataforma
        </Text>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {loginError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {loginError}
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <Fieldset>
              <Legend>Endereço de email</Legend>
              <div className="mt-2">
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Digite seu email"
                  required
                  disabled={loading}
                />
              </div>
            </Fieldset>

            <Fieldset>
              <Legend>Senha</Legend>
              <div className="mt-2 relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Digite sua senha"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </Fieldset>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 border-gray-300 rounded"
                  style={{ 
                    color: BRAND_COLOR,
                    '--tw-ring-color': BRAND_COLOR
                  } as React.CSSProperties}
                  disabled={loading}
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-white">
                  Lembrar de mim
                </label>
              </div>

              <div className="text-sm">
                <button 
                  type="button"
                  onClick={handleForgotPassword}
                  className="font-medium"
                  style={{ color: BRAND_COLOR }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = '#006d75'
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = BRAND_COLOR
                  }}
                  disabled={loading}
                >
                  Esqueceu sua senha?
                </button>
              </div>
            </div>

            <div>
              <Button 
                type="submit" 
                className="w-full text-white" 
                size="lg"
                style={{ backgroundColor: BRAND_COLOR }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.backgroundColor = '#006d75'
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.backgroundColor = BRAND_COLOR
                }}
                disabled={loading}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </div>

            <div className="text-center">
              <Text size="sm" color="muted">
                Não tem uma conta?{' '}
                <button 
                  type="button"
                  onClick={handleRegister}
                  className="font-medium"
                  style={{ color: BRAND_COLOR }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = '#006d75'
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = BRAND_COLOR
                  }}
                  disabled={loading}
                >
                  Registre-se
                </button>
              </Text>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Login
